// src/utils/weight.js
//
// An animal's weight, said ONE way everywhere (operator request
// 2026-10-07): "42 g" when the grams are whole, "4.5 g" otherwise, and
// "42 g · weighed Oct 7, 2026" with the day it was weighed.
//
// The value is DERIVED on the server (`facility/services/animal_weight`:
// the newest weigh-in or checkup event) and arrives as `current_weight_g`
// + `weighed_at`. NOTHING IS SAID WHEN THERE IS NO WEIGHT: every helper
// here answers `null` for a missing or non-numeric value, and the callers
// render nothing for `null` — no label, no placeholder, no "—" on a
// listing.
//
// A WEIGHT OF 0 OR LESS IS NOT A WEIGHT (review of 2026-10-07, SHOULD-1).
// The server's reader already skips one (readings warn, never refuse, so
// a 0 g weigh-in is STORED, but the current weight is the newest POSITIVE
// one); these helpers say nothing for one either, so no surface can ever
// print "0 g". `formatGrams` answers '' for it — a value that is there
// and is not a weight — and `null` for no value at all.
import { formatDateShort } from './dateUtils';

/** "42" / "4.5" — or null when there is no number. */
export const gramsNumber = (grams) => {
  if (grams === null || grams === undefined || grams === '') return null;
  const n = Number(grams);
  if (!Number.isFinite(n) || n <= 0) return null;
  const rounded = Math.round(n * 10) / 10;
  if (rounded <= 0) return null;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
};

/** "42 g" — '' for a number of 0 or less, null for no number at all. */
export const formatGrams = (grams) => {
  const n = gramsNumber(grams);
  if (n !== null) return `${n} g`;
  const raw = grams === null || grams === undefined || grams === '' ? NaN : Number(grams);
  return Number.isFinite(raw) ? '' : null;
};

/** "42 g · weighed Oct 7, 2026" (the date only when there is one) — or null. */
export const weightLine = (grams, weighedAt) => {
  const g = formatGrams(grams);
  if (!g) return null;
  return weighedAt ? `${g} · weighed ${formatDateShort(weighedAt)}` : g;
};

// SHOULD-1 (review, 2026-10-07): a weight of 0 or less is refused BEFORE
// anything is sent. The server would store it (readings warn, never
// refuse) and then skip it as no weight at all, so sending it logs an
// event nobody can see the point of; the sentence says how to remove a
// wrong weight instead. (It lived in `AnimalDetailsPanel` while the panel
// held the weight editor; the row's Log weight says it now.)
export const WEIGHT_NOT_POSITIVE = 'A weight has to be more than 0 g. '
  + "To remove a wrong weight, void that weigh-in in the animal's Activity.";

/** A draft that is not a number at all. */
export const WEIGHT_NOT_A_NUMBER = 'Type the weight in grams, as a number.';

/**
 * A weigh-in dated after today (today in the API's zone, `apiDay`). The
 * server refuses it too ("That event is dated in the future."), so this
 * is the client saying it first, in the keeper's words, with nothing sent.
 */
export const WEIGHT_IN_THE_FUTURE = 'A weigh-in cannot be dated in the future.';

// THE EVENT TYPES WHOSE `weight_g` IS THE ANIMAL'S WEIGHT — a declared
// PORT of `facility/services/animal_weight.WEIGHT_EVENT_TYPE_KEYS`
// (Cowork run 33, D1). An event of one of these logged or voided ABOUT an
// animal can change what its weight IS, so the grid re-reads it; any other
// event cannot, and costs no read.
export const WEIGHT_EVENT_TYPE_KEYS = ['weigh_in', 'checkup'];

/**
 * Does this event (a `POST events/` answer — `type.key` — or a timeline
 * entry — `type_key`) belong to a type that can carry the weight?
 */
export const eventCarriesWeight = (event) => {
  if (!event || typeof event !== 'object') return false;
  const key = event.type_key
    || (event.type && typeof event.type === 'object' ? event.type.key : null)
    || (typeof event.type === 'string' ? event.type : null);
  return WEIGHT_EVENT_TYPE_KEYS.includes(key);
};
